import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { KAPHY_BASE_URL } from "../../service/SignUpService";
import movingSign from "../../assets/images/moving-sign-arrow.png";
import "./AnnouncementPopup.css";

const SHOW_DELAY_MS = 700;

const TYPE_CONFIG = {
  office_move:   { icon: "fa-truck-moving",         headerGrad: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
  holiday_hours: { icon: "fa-clock",                headerGrad: "linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)" },
  closure:       { icon: "fa-door-closed",           headerGrad: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)" },
  new_service:   { icon: "fa-star",                  headerGrad: "linear-gradient(135deg, #22c55e 0%, #15803d 100%)" },
  general:       { icon: "fa-bullhorn",              headerGrad: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
  alert:         { icon: "fa-triangle-exclamation",  headerGrad: "linear-gradient(135deg, #f97316 0%, #c2410c 100%)" },
};

/**
 * AnnouncementPopup — public-facing popup.
 *
 * Normal usage: <AnnouncementPopup />   (fetches from API, honours localStorage dismissal)
 * Preview mode: <AnnouncementPopup previewData={...} onClose={fn} />  (bypasses API + localStorage)
 */
const AnnouncementPopup = ({ previewData = null, onClose: externalOnClose }) => {
  const [data,        setData]        = useState(null);
  const [visible,     setVisible]     = useState(false);
  const [animating,   setAnimating]   = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const dialogRef     = useRef(null);
  const closeButtonRef = useRef(null);
  const showTimerRef  = useRef(null);

  /* ── Dismissal check ── */
  const isDismissed = useCallback((ann) => {
    if (!ann?._id) return false;
    if (ann.dismissDurationDays === 0) return false; // always show
    const key       = `kaphy_ann_${ann._id}_${ann.dismissResetKey}`;
    const stored    = localStorage.getItem(key);
    if (!stored) return false;
    const durationMs = (ann.dismissDurationDays || 30) * 24 * 60 * 60 * 1000;
    return Date.now() - parseInt(stored, 10) < durationMs;
  }, []);

  /* ── Initial fetch / preview mode ── */
  useEffect(() => {
    if (previewData) {
      setData(previewData);
      setVisible(true);
      setAnimating(true);
      return;
    }

    const fetchAndShow = async () => {
      try {
        const res = await axios.get(`${KAPHY_BASE_URL}/api/announcement`);
        const ann = res.data;
        if (!ann?.isEnabled) return;
        if (isDismissed(ann)) return;
        showTimerRef.current = setTimeout(() => {
          setData(ann);
          setVisible(true);
          setAnimating(true);
        }, SHOW_DELAY_MS);
      } catch {
        /* silently suppress — popup is non-critical */
      }
    };

    fetchAndShow();
    return () => clearTimeout(showTimerRef.current);
  }, [previewData, isDismissed]);

  /* ── Focus management ── */
  useEffect(() => {
    if (visible && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [visible]);

  /* ── Keyboard handler ── */
  useEffect(() => {
    if (!visible) return;

    const handleKey = (e) => {
      if (e.key === "Escape") {
        dismiss();
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.disabled);
        if (!focusable.length) return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
        }
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  /* ── Scroll lock ── */
  useEffect(() => {
    if (visible) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [visible]);

  /* ── Dismiss ── */
  const dismiss = useCallback(() => {
    setAnimating(false);
    setTimeout(() => {
      setVisible(false);
      if (!previewData && data?._id && data.dismissDurationDays !== 0) {
        const key = `kaphy_ann_${data._id}_${data.dismissResetKey}`;
        localStorage.setItem(key, Date.now().toString());
      }
      if (externalOnClose) externalOnClose();
    }, 260);
  }, [data, previewData, externalOnClose]);

  if (!visible || !data) return null;

  const conf = TYPE_CONFIG[data.type] || TYPE_CONFIG.general;

  return (
    <div
      className={`ann-backdrop${animating ? " ann-backdrop-in" : " ann-backdrop-out"}`}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
      aria-hidden="true"
    >
      <div
        ref={dialogRef}
        className={`ann-dialog${animating ? " ann-dialog-in" : " ann-dialog-out"}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ann-dialog-title"
        aria-hidden="false"
      >
        {/* ── Header ── */}
        <div className="ann-header" style={{ background: conf.headerGrad }}>
          <div className="ann-header-icon" aria-hidden="true">
            <i className={`fa-solid ${conf.icon}`}></i>
          </div>
          <div className="ann-header-text">
            <h2 id="ann-dialog-title" className="ann-title">{data.title}</h2>
            {data.subtitle && <p className="ann-subtitle">{data.subtitle}</p>}
          </div>
          <button
            ref={closeButtonRef}
            className="ann-close"
            onClick={dismiss}
            aria-label="Close announcement"
          >
            <i className="fa-solid fa-times" aria-hidden="true"></i>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="ann-body">
          {/* Moving sign image */}
          {data.showImage && (
            <div
              className={`ann-image-wrap${imageLoaded ? "" : " ann-image-wrap-loading"}`}
            >
              {!imageLoaded && (
                <div className="ann-image-spinner" aria-hidden="true">
                  <i className="fa-solid fa-spinner fa-spin"></i>
                </div>
              )}
              <img
                src={movingSign}
                alt="Kaphy Insurance Agency — new office this way, Suite 31"
                className={`ann-image${imageLoaded ? " ann-image-loaded" : ""}`}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
              />
            </div>
          )}

          {/* Body message */}
          {data.message && (
            <p className="ann-message" style={{ whiteSpace: "pre-line" }}>
              {data.message}
            </p>
          )}

          {/* Location box — shown for office_move type */}
          {data.type === "office_move" &&
            (data.newLocationHighlight || data.newLocation) && (
              <div className="ann-location-box" role="region" aria-label="New office location">
                <div className="ann-location-label" aria-hidden="true">
                  <i className="fa-solid fa-location-dot"></i>
                  <span>New Location</span>
                </div>
                {data.newLocationHighlight && (
                  <div className="ann-suite-highlight">{data.newLocationHighlight}</div>
                )}
                {data.newLocation && (
                  <address className="ann-address">{data.newLocation}</address>
                )}
              </div>
            )}
        </div>

        {/* ── Footer ── */}
        <div className="ann-footer">
          {data.showDirectionsButton && data.directionsUrl && (
            <a
              href={data.directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ann-btn ann-btn-directions"
            >
              <i className="fa-solid fa-map-location-dot" aria-hidden="true"></i>
              <span>Get Directions</span>
            </a>
          )}
          <button className="ann-btn ann-btn-cta" onClick={dismiss}>
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
            <span>{data.ctaText || "Got It!"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPopup;
